import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import type { DeliveredRankRow } from '../api/types';
import { money } from '../utils/format';

import { AxisTick } from './AxisTick';
import { ChartTooltip } from './ChartTooltip';
import { CHROME, DIVERGE } from './palette';

const AXIS_WIDTH = 250;
const MAX_LABEL = 38;

/**
 * Places lost or gained once shipping is added. Signed magnitude around a zero
 * line, so it borrows the diverging scale: red where shipping pushes us down the
 * list, blue where it lifts us. The two ranks themselves live in the tooltip —
 * plotting both would be a slope chart answering a different question.
 */
export function RankShiftChart({ rows }: { rows: DeliveredRankRow[] }) {
  const data = rows.filter((row) => row.rankShift !== 0).slice(0, 14);

  if (data.length === 0) {
    return (
      <div className="state">
        <div>Shipping changes nobody&apos;s position.</div>
        <div className="muted">Either every offer ships for the same price, or none stated one.</div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, data.length * 30 + 40)}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 32, bottom: 4, left: 4 }} barCategoryGap={4}>
        <CartesianGrid stroke={CHROME.grid} horizontal={false} />
        <XAxis
          type="number"
          tick={{ fill: CHROME.inkMuted, fontSize: 11 }}
          axisLine={false}
          tickLine={false}
          tickFormatter={(value: number) => (value > 0 ? `+${value}` : String(value))}
        />
        <YAxis
          type="category"
          dataKey="name"
          axisLine={false}
          tickLine={false}
          width={AXIS_WIDTH}
          tick={(props) => <AxisTick {...props} max={MAX_LABEL} />}
        />
        <ReferenceLine x={0} stroke={CHROME.inkMuted} strokeWidth={1} />
        <Tooltip
          cursor={{ fill: 'rgba(255,255,255,0.05)' }}
          content={({ active, payload }) => {
            if (!active || !payload?.length) {
              return null;
            }

            const row = payload[0].payload as DeliveredRankRow;

            return (
              <ChartTooltip
                title={row.name}
                note={`#${row.productRank} on price → #${row.deliveredRank} delivered`}
                rows={[
                  { key: 'ours', label: 'Your price', value: money(row.ourPrice) },
                  { key: 'ship', label: 'Your shipping', value: money(row.ourShipping) },
                  { key: 'total', label: 'You, delivered', value: money(row.ourTotal), color: DIVERGE.high },
                  {
                    key: 'cheapest',
                    label: `Cheapest delivered${row.cheapestTotalShop ? ` (${row.cheapestTotalShop})` : ''}`,
                    value: money(row.cheapestTotal),
                    color: DIVERGE.low,
                  },
                  {
                    key: 'coverage',
                    label: 'Offers stating shipping',
                    value: `${row.competitorsWithShipping} of ${row.competitors}`,
                  },
                ]}
              />
            );
          }}
        />
        <Bar isAnimationActive={false} dataKey="rankShift" radius={4} maxBarSize={20}>
          {data.map((row) => (
            <Cell key={row.shopProductId} fill={row.rankShift > 0 ? DIVERGE.high : DIVERGE.low} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
